import { ResetPasswordDialog } from "./ResetPasswordDialog";

export interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (password: string) => void;
  /** Forwarded to the dialog. `false` opens it soft-disabled — see `ResetPasswordDialogProps`. */
  isWriteAvailable?: boolean;
}

/**
 * The reset-password dialog's open/closed gate (FR-017, FR-017b, FR-051).
 *
 * **Reachable from the page as of 2026-08-27, opened soft-disabled** (`isWriteAvailable={false}` from
 * `HeaderActions`). This supersedes the earlier decision to keep it unreachable, which had left Reset
 * Password greyed out with nothing anywhere explaining why — see `ResetPasswordDialog`'s docblock for
 * the full reasoning and for why the *fields* are disabled rather than just the confirm action.
 *
 * FR-051 is still true by construction, and by a stronger argument than before: with both fields
 * disabled no password value is ever created, so there is nothing to clear. The unmount-on-close
 * design below remains the mechanism that will keep FR-051 true once the endpoint lands and the
 * fields become live.
 *
 * **Why this is a separate component from the dialog it renders.** All of the state — and therefore
 * the password value — lives in `ResetPasswordDialog`, which this component mounts only while open.
 * Closing therefore *unmounts* the value rather than hiding it, which is what FR-017b and FR-051
 * actually require. The obvious one-component shape (`if (!isOpen) return null`) does **not** do this:
 * `return null` clears a component's DOM output but keeps its fiber and its `useState` alive, so the
 * old password is still there on reopen. It looked correct and passed every DOM-level assertion; only
 * "presents empty fields when reopened" caught it. Do not collapse these two files back into one.
 */
export const ResetPasswordModal = ({
  isOpen,
  onClose,
  onConfirm,
  isWriteAvailable
}: ResetPasswordModalProps) =>
  isOpen ? (
    <ResetPasswordDialog
      isWriteAvailable={isWriteAvailable}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  ) : null;
